import { t } from "@lingui/macro";
import { Check, X } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { useEffect, useState, RefObject } from "react";
import { IframePanel } from "./IframePanel";

type Props = {
  originalFrameRef: RefObject<HTMLIFrameElement>;
  optimizedFrameRef: RefObject<HTMLIFrameElement>;
  originalFrameLoaded: boolean;
  optimizedFrameLoaded: boolean;
  isDeleting: boolean;
  isAccepting: boolean;
  setShowRejectDialog: (open: boolean) => void;
  setShowApplyDialog: (open: boolean) => void;
  diffData: any[];
};

export const CompareIframes = ({
  originalFrameRef,
  optimizedFrameRef,
  originalFrameLoaded,
  optimizedFrameLoaded,
  isDeleting,
  isAccepting,
  setShowRejectDialog,
  setShowApplyDialog,
  diffData,
}: Props) => {
  const [iframeHeight, setIframeHeight] = useState({
    original: 1200,
    optimized: 1200,
  });
  const [iframeWidth, setIframeWidth] = useState<number>(600);

  // Resize when width changed
  useEffect(() => {
    const iframeWidth = originalFrameRef.current?.getBoundingClientRect().width || 600;
    setIframeWidth(iframeWidth);

    let resizeObserver: ResizeObserver | null = null;
    let resizeTimeout: NodeJS.Timeout | null = null;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        // Debounce resize events to avoid too many height updates
        if (resizeTimeout) clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          const newWidth = originalFrameRef.current?.getBoundingClientRect().width || 600;
          setIframeWidth(newWidth);
        }, 200);
      });
      resizeObserver.observe(document.body);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, []);

  // Listen to height change event
  useEffect(() => {
    const handleHeightMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data.type === "CONTENT_HEIGHT") {
        const { height, frameId } = event.data.payload;
        if (frameId !== "original" && frameId !== "optimized") {
          console.error("Invalid frameId", frameId);
        }

        const newHeight = Math.max(height + 20, 1200);
        const iframeWidth = originalFrameRef.current?.getBoundingClientRect().width || 600;
        setIframeHeight((heights) => ({ ...heights, [frameId]: newHeight }));
        setIframeWidth(iframeWidth);

        const iframeHeightAdjustedMessage = {
          type: "CONTENT_HEIGHT_ADJUSTED",
          payload: {
            height: newHeight,
            frameId,
          },
        };
        originalFrameRef.current?.contentWindow?.postMessage(iframeHeightAdjustedMessage, "*");
        optimizedFrameRef.current?.contentWindow?.postMessage(iframeHeightAdjustedMessage, "*");
      }
    };

    window.addEventListener("message", handleHeightMessage);

    return () => {
      window.removeEventListener("message", handleHeightMessage);
    };
  }, []);

  const realHeight = Math.max(iframeHeight.original, iframeHeight.optimized);
  return (
    <motion.div
      className="grid h-full min-h-[100vw] grid-cols-2 gap-4"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
    >
      {/* Original Resume Iframe */}
      <IframePanel
        header={{
          title: t`Original Resume`,
          onClick: () => setShowRejectDialog(true),
          className: "bg-red-500",
          isLoading: isDeleting || isAccepting,
          loadingLabel: t`Rejecting...`,
          btnLabel: t`Reject`,
          Icon: X,
        }}
        frameRef={originalFrameRef}
        frameLoaded={originalFrameLoaded}
        iframeHeight={realHeight}
        iframeWidth={iframeWidth}
        dataFrame="original"
      />

      {/* Optimized Resume Iframe */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut", delay: 0.6 }}
      >
        <IframePanel
          header={{
            title: t`Optimized Resume`,
            onClick: () => setShowApplyDialog(true),
            className: "bg-green-600",
            isLoading: isDeleting || isAccepting,
            loadingLabel: t`Applying...`,
            btnLabel: t`Apply`,
            Icon: Check,
          }}
          frameRef={optimizedFrameRef}
          frameLoaded={optimizedFrameLoaded}
          iframeHeight={realHeight}
          iframeWidth={iframeWidth}
          dataFrame="optimized"
        />
      </motion.div>
    </motion.div>
  );
};
